import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Contact DN Biotech | Research Peptide Quotes & Support',
    description: 'Contact DN Biotech for research peptide quotes, technical questions, and order support. Email, WhatsApp, and inquiry form available. Response within 2 hours during business hours.',
    keywords: 'contact DN Biotech, peptide quotes, research peptide inquiry, laboratory supply contact, biotech support',
    canonical: `${siteUrl}/contact`,
    schema: generateWebPageSchema(siteUrl, '/contact', 'Contact DN Biotech | Research Peptide Quotes & Support', 'Contact DN Biotech for research peptide quotes, technical questions, and order support. Email, WhatsApp, and inquiry form available. Response within 2 hours during business hours.')
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d63dd82bdoed7v0v8n3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });
      setFormStatus('success');
      e.currentTarget.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-biotech">
        <div className="container-custom text-center">
          <h1 className="text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto">
            Request quotes, ask technical questions, or get support from our team
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="mb-6">Contact Information</h2>
              <p className="text-navy-600 leading-relaxed mb-10">
                Whether you need product quotes, technical guidance, or order assistance, our team is ready to help. We typically respond within 2 hours during business hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-50 rounded-xl flex-shrink-0 border border-primary-100">
                    <i className="ri-mail-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-900 mb-1">Email</h3>
                    <a href="mailto:support@shopdailynest.com" className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium">
                      support@shopdailynest.com
                    </a>
                    <p className="text-sm text-navy-600 mt-1">Response time: Under 2 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
                    <i className="ri-whatsapp-line text-xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-900 mb-1">WhatsApp</h3>
                    <a
                      href="https://wa.me/639178850200"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 cursor-pointer font-medium"
                    >
                      Chat with us
                    </a>
                    <p className="text-sm text-navy-600 mt-1">Available 24/7 for urgent inquiries</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-50 rounded-xl flex-shrink-0 border border-primary-100">
                    <i className="ri-map-pin-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-900 mb-1">Business Address</h3>
                    <p className="text-navy-600">1302 Waugh Dr. #413</p>
                    <p className="text-navy-600">Houston, TX 77019, USA</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-50 rounded-xl flex-shrink-0 border border-primary-100">
                    <i className="ri-time-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-900 mb-1">Business Hours</h3>
                    <p className="text-navy-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                    <p className="text-navy-600">Saturday - Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="mt-10 card">
                <div className="card-body">
                  <h3 className="text-lg font-bold text-navy-900 mb-4">Accepted Payment Methods</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-bit-coin-line text-xl text-primary-600"></i>
                      <span className="text-navy-700 font-medium">BTC</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-currency-line text-xl text-primary-600"></i>
                      <span className="text-navy-700 font-medium">ETH</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-coin-line text-xl text-primary-600"></i>
                      <span className="text-navy-700 font-medium">USDT</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-bank-card-line text-xl text-primary-600"></i>
                      <span className="text-navy-700 font-medium">Credit Card</span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <i className="ri-paypal-line text-xl text-primary-600"></i>
                      <span className="text-navy-700 font-medium">PayPal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="card">
                <div className="card-body">
                  <h2 className="mb-6">Send a Message</h2>
                  
                  {formStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 flex items-center font-medium">
                        <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                        Message sent successfully! We'll respond soon.
                      </p>
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 flex items-center font-medium">
                        <i className="ri-error-warning-line text-xl mr-2"></i>
                        Failed to send message. Please try again.
                      </p>
                    </div>
                  )}

                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div>
                      <label className="label">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">
                        Institution / Organization
                      </label>
                      <input
                        type="text"
                        name="institution"
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">
                        Subject *
                      </label>
                      <select
                        name="subject"
                        required
                        className="input"
                      >
                        <option value="">Select a subject</option>
                        <option value="quote">Request a Quote</option>
                        <option value="product">Product Inquiry</option>
                        <option value="technical">Technical Question</option>
                        <option value="order">Order Status</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="label">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="input resize-none"
                        placeholder="Please provide details about your inquiry..."
                      ></textarea>
                      <p className="text-xs text-navy-500 mt-2">Maximum 500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="btn btn-primary w-full btn-lg"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <i className="ri-send-plane-line ml-2"></i>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}